import {
  createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { AWAY_WINDOW_CHANGED_EVENT, getMyAway, type AwayWindow } from '../api/away';
import { useAuth } from './AuthContext';
import { isWindowVisible } from '../utils/windowVisibility';

/**
 * Slow safety net only. The quiet-mode flag is otherwise event-driven: the
 * user's own away/DND system frames on the shared socket, the local
 * AWAY_WINDOW_CHANGED_EVENT, window focus, and a timer armed for the exact
 * end of an active window. (This replaced an unconditional 30-second poll of
 * /away/me that ran for the app's whole lifetime, tray or not.)
 */
export const DND_QUIET_SAFETY_NET_MS = 5 * 60_000;

type DndQuietModeApi = {
  isDndQuiet: boolean;
  refreshDndQuietMode: () => Promise<boolean>;
};

const DEFAULT_VALUE: DndQuietModeApi = {
  isDndQuiet: false,
  refreshDndQuietMode: async () => false,
};

const DndQuietModeContext = createContext<DndQuietModeApi>(DEFAULT_VALUE);

export function isActiveDndWindow(window: AwayWindow | null, now = Date.now()): boolean {
  if (!window || window.status_type !== 'dnd' || window.ended_at) return false;
  const startsAt = Date.parse(window.starts_at);
  const endsAt = Date.parse(window.ends_at);
  return Number.isFinite(startsAt) && Number.isFinite(endsAt) && startsAt <= now && now < endsAt;
}

/**
 * Milliseconds until an active DND window ends (so a single timer can flip
 * quiet mode off on time), or null when there is nothing scheduled.
 */
export function msUntilDndWindowEnd(window: AwayWindow | null, now = Date.now()): number | null {
  if (!isActiveDndWindow(window, now)) return null;
  const endsAt = Date.parse(window!.ends_at);
  return Math.max(0, endsAt - now);
}

/**
 * A client-side fail-safe for a real DND window. The server remains
 * authoritative for delivery, while this prevents any locally delivered frame
 * from creating sound, a native alert, or an incoming-call screen.
 */
export function DndQuietModeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [isDndQuiet, setIsDndQuiet] = useState(false);
  const [activeWindow, setActiveWindow] = useState<AwayWindow | null>(null);

  const refreshDndQuietMode = useCallback(async (): Promise<boolean> => {
    if (!user?.id) {
      setIsDndQuiet(false);
      setActiveWindow(null);
      return false;
    }
    try {
      const window = await getMyAway();
      const next = isActiveDndWindow(window);
      setIsDndQuiet(next);
      setActiveWindow(next ? window : null);
      return next;
    } catch {
      // Server-side call and mention guards remain the source of truth when a
      // refresh is temporarily unavailable.
      setIsDndQuiet(false);
      setActiveWindow(null);
      return false;
    }
  }, [user?.id]);

  useEffect(() => {
    void refreshDndQuietMode();
    const onFocus = () => { void refreshDndQuietMode(); };
    const onAwayWindowChange = () => { void refreshDndQuietMode(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener(AWAY_WINDOW_CHANGED_EVENT, onAwayWindowChange);
    const safetyNet = window.setInterval(() => {
      if (document.visibilityState === 'hidden' || !isWindowVisible()) return;
      void refreshDndQuietMode();
    }, DND_QUIET_SAFETY_NET_MS);
    return () => {
      window.clearInterval(safetyNet);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(AWAY_WINDOW_CHANGED_EVENT, onAwayWindowChange);
    };
  }, [refreshDndQuietMode]);

  // Flip quiet mode off at the window's scheduled end without polling for it.
  useEffect(() => {
    const ms = msUntilDndWindowEnd(activeWindow);
    if (ms === null) return;
    const timer = window.setTimeout(() => { void refreshDndQuietMode(); }, ms + 250);
    return () => window.clearTimeout(timer);
  }, [activeWindow, refreshDndQuietMode]);

  const value = useMemo(() => ({ isDndQuiet, refreshDndQuietMode }), [isDndQuiet, refreshDndQuietMode]);
  return <DndQuietModeContext.Provider value={value}>{children}</DndQuietModeContext.Provider>;
}

export function useDndQuietMode(): DndQuietModeApi {
  return useContext(DndQuietModeContext);
}
